import { enumeratePlacements, type Placement } from "./placements";
import type { PackItem } from "./types";

/**
 * Build the 2D-packing problem as a set-packing 0/1 integer program in CPLEX
 * LP format (the format HiGHS reads):
 *
 *   maximize    Σ value_p · x_p
 *   subject to  Σ_{p over cell} x_p ≤ 1   (no two pieces share a cell)
 *               Σ_{p of item i} x_p ≤ 1   (each item placed at most once)
 *               x_p ∈ {0, 1}
 *
 * Returns the LP string and the placement table needed to decode the solution.
 */
export function buildHighsLP(
  items: PackItem[],
  cols: number,
  rows: number,
): { lp: string; placements: Placement[] } {
  const { placements, cellCover, itemPlace } = enumeratePlacements(items, cols, rows);
  const P = placements.length;

  let lp = "\\ 2D bin packing - set-packing MILP (HiGHS)\n";
  lp += "\\ Grid " + rows + "x" + cols + ", " + items.length + " items, " + P + " placements\n";
  lp += "Maximize\n obj: " + placements.map((pl, p) => `${pl.value} x${p}`).join(" + ") + "\n";
  lp += "Subject To\n";
  for (const [k, ps] of Object.entries(cellCover)) {
    if (ps.length > 1) {
      lp += ` cell_${k.replace(",", "_")}: ${ps.map((p) => `x${p}`).join(" + ")} <= 1\n`;
    }
  }
  for (let i = 0; i < itemPlace.length; i++) {
    if (itemPlace[i].length > 0) {
      lp += ` item_${i}: ${itemPlace[i].map((p) => `x${p}`).join(" + ")} <= 1\n`;
    }
  }
  lp += "Bounds\n";
  for (let p = 0; p < P; p++) lp += ` 0 <= x${p} <= 1\n`;
  lp += "Binary\n";
  for (let p = 0; p < P; p++) lp += ` x${p}\n`;
  lp += "End\n";

  return { lp, placements };
}
